import { Box, Button, Typography } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { useUpdateZoneZonesIdPatchMutation } from "api";
import { SwitchInput, TimeInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import {
  DEFAULT_NIGHT_REPORT_END,
  DEFAULT_NIGHT_REPORT_START,
} from "interfaces/nightReport.interface";
import { IZone } from "interfaces/zone.interface";

const zoneSettingsSchema = z.object({
  isCustom: z.boolean(),
  night_report_times: z
    .object({
      start: z.string(),
      end: z.string(),
    })
    .nullable()
    .optional()
    .default(null),
});

type ZoneSettingsSchema = z.infer<typeof zoneSettingsSchema>;

export const findAncestorWithNightReportTimes = (
  zone: IZone,
  zones: IZone[],
): IZone => {
  if (!zone.parent_id) return zone;

  const parentZone = zones.find((z) => z.id === zone.parent_id);

  if (parentZone === undefined) return zone;

  if (parentZone.settings?.night_report_times) {
    return parentZone;
  }
  return findAncestorWithNightReportTimes(parentZone, zones);
};

type NightReportSettingsProps = {
  zone: IZone;
  zones: IZone[];
};

export function NightReportSettings({ zone, zones }: NightReportSettingsProps) {
  const { t } = useTranslation();

  const [updateZone] = useUpdateZoneZonesIdPatchMutation();

  const parentZone = findAncestorWithNightReportTimes(zone, zones);

  const { handleSubmit, control, watch, resetField } = useForm({
    resolver: zodResolver(zoneSettingsSchema),
    defaultValues: zoneSettingsSchema.parse({
      isCustom: !!zone.settings?.night_report_times,
      night_report_times: zone.settings?.night_report_times ??
        parentZone?.settings?.night_report_times ?? {
          start: DEFAULT_NIGHT_REPORT_START,
          end: DEFAULT_NIGHT_REPORT_END,
        },
    }),
  });

  const onSubmit = (data: ZoneSettingsSchema) => {
    updateZone({
      id: zone.id,
      zoneUpdate: {
        settings: {
          night_report_times: data.isCustom ? data.night_report_times : null,
        },
      },
    });
  };

  const isCustom = watch("isCustom");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SectionCard title={t("night_report_settings")}>
        <Typography>{t("night_report_time")}</Typography>
        <Box
          border={1}
          borderColor={"divider"}
          borderRadius={2}
          padding={2}
          display={"flex"}
          flexDirection={"column"}
          gap={2}
        >
          <Box display={"flex"} gap={2} alignItems={"center"}>
            <SwitchInput
              label={t("custom_night_report_times")}
              fieldName={"isCustom"}
              control={control}
              onChange={(e) => {
                if (!e.target.checked) {
                  resetField("night_report_times");
                }
              }}
            />
            <Box flexGrow={1} />
            {!isCustom && parentZone.id !== zone.id && (
              <Typography>
                {t("inherited_from") + " " + parentZone.name}
              </Typography>
            )}
          </Box>
          <Box display={"flex"} gap={1}>
            <Box>
              <TimeInput
                label={t("From")}
                fieldName={"night_report_times.start"}
                includeSeconds={false}
                control={control}
                disabled={!isCustom}
              />
            </Box>
            <Box>
              <TimeInput
                label={t("To")}
                fieldName={"night_report_times.end"}
                includeSeconds={false}
                control={control}
                disabled={!isCustom}
              />
            </Box>
          </Box>
        </Box>
        <Button type={"submit"}>{t("Save")}</Button>
      </SectionCard>
    </form>
  );
}
